import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, type ActivityEntry } from '../lib/api';
import { useTicketEvents } from '../lib/events';
import { Dialog } from './ui/Dialog';
import { Badge } from './Badge';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 50;

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function describeEvent(
  e: Extract<ActivityEntry, { kind: 'ticket_event' }>,
): { verb: string; detail: string | null } {
  const from = e.fromValue as Record<string, unknown> | null;
  const to = e.toValue as Record<string, unknown> | null;
  switch (e.eventType) {
    case 'CREATED':
      return { verb: 'created', detail: null };
    case 'STATUS_CHANGED':
      return {
        verb: 'changed status',
        detail:
          from?.status && to?.status
            ? `${String(from.status).replace(/_/g, ' ')} → ${String(to.status).replace(/_/g, ' ')}`
            : null,
      };
    case 'PRIORITY_CHANGED':
      return {
        verb: 'changed priority',
        detail: from?.priority && to?.priority ? `${from.priority} → ${to.priority}` : null,
      };
    case 'ASSIGNEE_CHANGED':
      return { verb: 'reassigned', detail: typeof to?.name === 'string' ? `to ${to.name}` : null };
    case 'AREA_CHANGED':
      return {
        verb: 'changed area',
        detail: from?.area && to?.area ? `${from.area} → ${to.area}` : null,
      };
    case 'REPORTER_CHANGED':
      return { verb: 'changed reporter', detail: typeof to?.name === 'string' ? `to ${to.name}` : null };
    case 'BLAME_CHANGED':
      return { verb: 'updated cause', detail: null };
    case 'CUSTOMER_SERVICE_CHANGED':
      return { verb: to?.isCustomerService ? 'marked CS' : 'unmarked CS', detail: null };
    case 'LINK_ADDED':
      return { verb: 'linked', detail: typeof to?.linkType === 'string' ? to.linkType.toLowerCase() : null };
    case 'LINK_REMOVED':
      return { verb: 'unlinked', detail: null };
    case 'COMMENT_ADDED':
      return { verb: 'commented', detail: null };
    case 'ATTACHMENT_ADDED':
      return { verb: 'attached', detail: typeof to?.filename === 'string' ? to.filename : null };
    case 'EMAIL_SENT':
      return { verb: 'emailed', detail: typeof to?.kind === 'string' ? to.kind.replace(/_/g, ' ') : null };
    default:
      return { verb: e.eventType.toLowerCase().replace(/_/g, ' '), detail: null };
  }
}

export function ActivityDrawer({ open, onClose }: Props) {
  const [events, setEvents] = useState<ActivityEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [, force] = useState(0); // re-render every minute so relative times stay fresh
  // Debounce SSE-driven refetches so a burst of events doesn't hammer the API.
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Activity.list({ limit: PAGE_SIZE });
      setEvents(res.events);
      setNextCursor(res.nextCursor);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void refetch();
  }, [open, refetch]);

  // Live updates: when SSE fires anything, debounce-refetch.
  useTicketEvents(() => {
    if (!open) return;
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      void refetch();
    }, 400);
  });

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [open]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await Activity.list({ limit: PAGE_SIZE, before: nextCursor });
      setEvents((prev) => [...prev, ...res.events]);
      setNextCursor(res.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      variant="drawer"
      title="Activity"
      bodyClassName=""
    >
          {loading && events.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">Loading…</p>
          )}
          {!loading && events.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">No activity yet.</p>
          )}
          <ul className="divide-y divide-slate-100">
            {events.map((e) => {
              if (e.kind === 'suggestion_created') {
                return (
                  <li key={e.id} className="px-4 py-2.5 text-sm hover:bg-slate-50" onClick={onClose}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-slate-500 text-xs">{fmtTime(e.createdAt)}</span>
                      <Badge tone="active">Suggestion</Badge>
                    </div>
                    <div className="text-slate-800 leading-snug">
                      <span className="font-medium">{e.actor.name}</span>{' '}
                      <span className="text-slate-600">suggested</span>{' '}
                      <Link
                        to="/suggestions"
                        className="text-brand-600 hover:underline font-medium"
                      >
                        {e.suggestion.title}
                      </Link>
                    </div>
                  </li>
                );
              }
              const { verb, detail } = describeEvent(e);
              return (
                <li key={e.id} className="px-4 py-2.5 text-sm hover:bg-slate-50" onClick={onClose}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-slate-500 text-xs">{fmtTime(e.createdAt)}</span>
                  </div>
                  <div className="text-slate-800 leading-snug">
                    <span className="font-medium">{e.actor.name}</span>{' '}
                    <span className="text-slate-600">{verb}</span>{' '}
                    <Link
                      to={`/tickets/${e.ticket.id}`}
                      className="text-brand-600 hover:underline font-medium"
                    >
                      #{e.ticket.id}
                    </Link>
                    <span className="text-slate-600"> — </span>
                    <span className="text-slate-700">{e.ticket.title}</span>
                  </div>
                  {detail && <div className="text-xs text-slate-500 mt-0.5">{detail}</div>}
                </li>
              );
            })}
          </ul>
          {nextCursor && (
            <div className="p-3">
              <button
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="w-full px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
    </Dialog>
  );
}
